<template>
  <v-container class="mt-4">
    <v-row justify-center v-resize="onResize">
      <v-col cols="isMobile ? 12 : 9">
        <v-breadcrumbs :items="breadcrumbs" class="px-0 pt-0">
          <template v-slot:divider>
            <v-icon>mdi-chevron-right</v-icon>
          </template>
        </v-breadcrumbs>
        <h1 class="mb-3" :class="{ 'text-h6': isMobile, 'text-h5': !isMobile }">{{ $t('cpxReport.title', [campaignName]) }}</h1>
      </v-col>
      <v-col class="text-right" :cols="isMobile ? 12 : 3">
        <v-btn color="light-blue darken-1 text-white" @click="clickCreativeReport()">{{ $t('creativeReport.buttonToCreativeReport') }}</v-btn>
      </v-col>
    </v-row>

    <v-row justify-center>
      <v-col cols="12" v-if="errorMessage">
        <v-alert :value="true" type="error" v-html="$t(errorMessage)"></v-alert>
      </v-col>
      <v-col cols="12">
        <div class="mb-2" :class="{ title: !isMobile, subheading: isMobile }" :v-html="$t('cpxReport.info.title')"></div>
        <v-data-table
          :headers="infoHeaders"
          :items="info"
          :hide-default-header="isMobile"
          :no-data-text="$t('commons.table.no.data.text')"
          hide-default-footer
          class="elevation-3"
          :class="{ mobile: isMobile }"
        >
          <template v-slot:item="props">
            <tr v-if="!isMobile">
              <td class="text-center break-word-cell">{{ props.item.advertiserName }}</td>
              <td class="text-center">{{ props.item.campaignName }}</td>
              <td class="text-center">{{ props.item.cpxreportType }}</td>
              <td class="text-right">{{ number_format_yen(props.item.periodGoalBudget) }}</td>
              <td class="text-right">{{ number_format(props.item.periodGoalCount) }}</td>
              <td class="text-right">{{ number_format_yen(props.item.cpxReportPrice) }}</td>
            </tr>
            <tr v-else>
              <td>
                <ul class="flex-content">
                  <li class="flex-item word-break" :data-label="$t('cpxReport.info.headers.advertiserName')">{{ props.item.advertiserName }}</li>
                  <li class="flex-item word-break" :data-label="$t('cpxReport.info.headers.campaignName')">{{ props.item.campaignName }}</li>
                  <li class="flex-item2" :data-label="$t('cpxReport.info.headers.cpxreportType')">{{ props.item.cpxreportType }}</li>
                  <li class="flex-item2" :data-label="$t('cpxReport.info.headers.periodGoalBudget')">
                    {{ number_format_yen(props.item.periodGoalBudget) }}
                  </li>
                  <li class="flex-item2" :data-label="$t('cpxReport.info.headers.periodGoalCountCV')">
                    {{ number_format(props.item.periodGoalCount) }}
                  </li>
                  <li class="flex-item2" :data-label="$t('cpxReport.info.headers.cpxReportPriceCV')">
                    {{ number_format_yen(props.item.cpxReportPrice) }}
                  </li>
                </ul>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-row justify-left class="mt-3">
      <v-col cols="12">
        <div class="mb-2" :class="{ title: !isMobile, subheading: isMobile }" :v-html="$t('cpxReport.total.title')"></div>
        <v-data-table
          :headers="totalHeaders"
          :items="total"
          :hide-default-header="isMobile"
          :no-data-text="$t('commons.table.no.data.text')"
          hide-default-footer
          class="elevation-3"
          :class="{ mobile: isMobile }"
        >
          <template v-slot:item="props">
            <tr v-if="!isMobile">
              <td class="text-center">{{ props.item.periodStartAt }} - {{ props.item.periodEndAt }}</td>
              <td class="text-right">{{ number_format(props.item.totalCount) }}</td>
              <td class="text-right">{{ number_format_yen(props.item.totalDigestionAmount) }}</td>
            </tr>
            <tr v-else>
              <td>
                <ul class="flex-content">
                  <li class="flex-item word-break" :data-label="$t('cpxReport.total.headers.periodCV')">
                    {{ props.item.periodStartAt }} - {{ props.item.periodEndAt }}
                  </li>
                  <li class="flex-item3" :data-label="$t('cpxReport.total.headers.totalCountCV')">{{ number_format(props.item.totalCount) }}</li>
                  <li class="flex-item4" :data-label="$t('cpxReport.total.headers.totalDigestionAmountCV')">
                    {{ number_format_yen(props.item.totalDigestionAmount) }}
                  </li>
                </ul>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row justify-left class="mt-3">
      <v-col cols="12" class="text-right">
        <v-btn color="light-green darken-1 text-white" @click="clickDownload()" :disabled="isDownloadCsv">
          <v-icon>mdi-download</v-icon>
          {{ $t('cpxReport.dailyReport.download') }}
        </v-btn>
        <div class="caption text-red-accent-4">{{ $t('cpxReport.dailyReport.infoMessage') }}</div>
      </v-col>
    </v-row>
    <v-row justify-left>
      <v-col cols="12">
        <v-data-table
          :headers="dailyReportHeaders"
          :items="dailyReport"
          :hide-default-header="isMobile"
          :no-data-text="$t('commons.table.no.data.text')"
          hide-default-footer
          :sort-by="pagination.sortBy"
          :sortDesc="pagination.descending"
          :items-per-page="pagination.rowsPerPage"
          class="elevation-3"
          :class="{ mobile: isMobile }"
        >
          <template v-slot:item="props">
            <tr v-if="!isMobile">
              <td class="text-center break-word-cell">{{ props.item.day }}</td>
              <td class="text-right">{{ number_format(props.item.imp) }}</td>
              <td class="text-right">{{ number_format(props.item.click) }}</td>
              <td class="text-right">{{ percent_format(props.item.ctr) }}</td>
              <td class="text-right">{{ number_format(props.item.cv) }}</td>
              <td class="text-right">{{ number_format_yen(props.item.digestionAmount) }}</td>
            </tr>
            <tr v-else>
              <td>
                <ul class="flex-content">
                  <li class="flex-item3" :data-label="$t('cpxReport.dailyReport.headers.day')">{{ props.item.day }}</li>
                  <li class="flex-item4" :data-label="$t('cpxReport.dailyReport.headers.imp')">{{ number_format(props.item.imp) }}</li>
                  <li class="flex-item3" :data-label="$t('cpxReport.dailyReport.headers.click')">{{ number_format(props.item.click) }}</li>
                  <li class="flex-item4" :data-label="$t('cpxReport.dailyReport.headers.ctr')">{{ percent_format(props.item.ctr) }}</li>
                  <li class="flex-item3" :data-label="$t('cpxReport.dailyReport.headers.cv')">{{ number_format(props.item.cv) }}</li>
                  <li class="flex-item4" :data-label="$t('cpxReport.dailyReport.headers.digestionAmountCV')">
                    {{ number_format_yen(props.item.digestionAmount) }}
                  </li>
                </ul>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row justify-end>
      <v-col class="text-right memo-text py-1 px-1">ad_{{ advertiserId }} stp_{{ this.campaignPeriodId }}</v-col>
    </v-row>
    <v-dialog v-model="nodataDialog" scrollable max-width="300px">
      <v-card>
        <v-card-title :v-html="$t('cpxReport.dialog.filedownload.title')"></v-card-title>
        <v-divider></v-divider>
        <v-card-text height="300px" :v-html="$t('cpxReport.dialog.filedownload.nodata')"></v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="nodataDialog = false" :v-html="$t('cpxReport.dialog.filedownload.close')"></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import CsvDownload from '../plugins/csvDownload'
import { useFormatters } from '../plugins/format';

export default {
  name: 'CpaReport',
  props: ['campaignPeriodId'],
  mixins: [CsvDownload],
  setup() {
    const {
      number_format,
      number_format_yen,
      percent_format,
    } = useFormatters();

    return {
      number_format,
      number_format_yen,
      percent_format,
    };
  },
  data: function () {
    return {
      pagination: {
        sortBy: [{ key: 'day', order:'asc' }],
        descending: false,
        rowsPerPage: -1
      },
      nodataDialog: false,
      isDownloadCsv: false,
      infoHeaders: [
        {
          title: this.$t('cpxReport.info.headers.advertiserName'),
          align: 'center',
          sortable: false,
          key: 'advertiserName'
        },
        {
          title: this.$t('cpxReport.info.headers.campaignName'),
          align: 'center',
          sortable: false,
          key: 'campaignName'
        },
        {
          title: this.$t('cpxReport.info.headers.cpxreportType'),
          align: 'center',
          sortable: false,
          key: 'cpxreportType'
        },
        {
          title: this.$t('cpxReport.info.headers.periodGoalBudget'),
          align: 'center',
          sortable: false,
          key: 'periodGoalBudget'
        },
        {
          title: this.$t('cpxReport.info.headers.periodGoalCountCV'),
          align: 'center',
          sortable: false,
          key: 'periodGoalCount'
        },
        {
          title: this.$t('cpxReport.info.headers.cpxReportPriceCV'),
          align: 'center',
          sortable: false,
          key: 'cpxReportPrice'
        }
      ],
      info: [],
      totalHeaders: [
        {
          title: this.$t('cpxReport.total.headers.periodCV'),
          align: 'center',
          sortable: false,
          key: 'campaignPeriod',
          width: '200'
        },
        {
          title: this.$t('cpxReport.total.headers.totalCountCV'),
          align: 'center',
          sortable: false,
          key: 'cv',
          width: '200'
        },
        {
          title: this.$t('cpxReport.total.headers.totalDigestionAmountCV'),
          align: 'center',
          sortable: false,
          key: 'digestionAmount',
          width: '200'
        }
      ],
      total: [],
      dailyReportHeaders: [
        {
          title: this.$t('cpxReport.dailyReport.headers.day'),
          align: 'center',
          key: 'day',
          width: '150'
        },
        {
          title: this.$t('cpxReport.dailyReport.headers.imp'),
          align: 'center',
          key: 'imp',
          width: '200'
        },
        {
          title: this.$t('cpxReport.dailyReport.headers.click'),
          align: 'center',
          key: 'click',
          width: '150'
        },
        {
          title: this.$t('cpxReport.dailyReport.headers.ctr'),
          align: 'center',
          key: 'ctr',
          width: '100'
        },
        {
          title: this.$t('cpxReport.dailyReport.headers.cv'),
          align: 'center',
          key: 'cv',
          width: '150'
        },
        {
          title: this.$t('cpxReport.dailyReport.headers.digestionAmountCV'),
          align: 'center',
          key: `digestionAmount`,
          width: '160'
        }
      ],
      dailyReport: [],
      isMobile: false,
      errorMessage: undefined
    }
  },
  methods: {
    ...mapActions('report', ['getReport']),
    initialize: function () {
      this.previewReport()
    },
    previewReport: function () {
      const jwtToken = this.$store.getters['auth/jwtToken']
      const campaignPeriodId = this.campaignPeriodId
      this.getReport({
        privateCode: process.env.ProjectCode,
        campaignPeriodId: campaignPeriodId,
        jwtToken: jwtToken
      })
        .then((result) => {
          this.errorMessage = undefined
          if (!result) {
            // 表示するデータがない場合
            this.info = []
            this.total = []
            this.dailyReport = []
            return
          }
          this.info = [result.info]
          this.total = [result.total]
          this.dailyReport = result.dailyReport
        })
        .catch((err) => {
          if (err instanceof ReferenceError) {
            this.errorMessage = err.message
          } else {
            // idToken が切れているのでセッションを切り、ログイン画面に遷移する
            this.$store.dispatch('auth/logout')
            this.$router.go({ path: this.$router.currentRoute.path, force: true })
          }
        })
    },
    clickDownload: function () {
      this.isDownloadCsv = true
      const dailyReport = this.$store.getters['report/dailyReport']
      if (!dailyReport || dailyReport.length <= 0) {
        this.nodataDialog = true
        this.isDownloadCsv = false
        return
      }
      let csvList = []
      csvList.push(
        this.$t('cpxReport.dailyReport.headers.day') +
          ',' +
          this.$t('cpxReport.dailyReport.headers.imp') +
          ',' +
          this.$t('cpxReport.dailyReport.headers.click') +
          ',' +
          this.$t('cpxReport.dailyReport.headers.ctr') +
          ',' +
          this.$t('cpxReport.dailyReport.headers.cv') +
          ',' +
          this.$t('cpxReport.dailyReport.headers.digestionAmountCV')
      )
      dailyReport.forEach((el) => {
        const imp = el['imp'] !== null ? el['imp'] : ''
        const click = el['click'] !== null ? el['click'] : ''
        const ctr = el['ctr'] !== null ? el['ctr'] : ''
        const cv = el['cv'] !== null ? el['cv'] : ''
        const digestionAmount = el['digestionAmount'] !== null ? el['digestionAmount'] : ''
        const line = `${el['day']},` + imp + `,` + click + `,` + ctr + `,` + cv + `,` + digestionAmount
        csvList.push(line)
      })
      this.csvDownload(csvList, 'cpa_report', [this.advertiserId, this.campaignPeriodId])
      this.isDownloadCsv = false
    },
    clickCreativeReport: function () {
      this.$router.push({
        path: `/creativeReportList/${this.agencyId}/${this.advertiserId}`,
        query: {
          cIds: this.info[0]['campaignId'],
          from: this.formatParameterDate(this.total[0]['periodStartAt']),
          to: this.formatParameterDate(this.total[0]['periodEndAt'])
        }
      })
    },
    formatDate(value, format) {
      return moment(value, 'YYYY/MM/DD').format(format)
    },
    formatParameterDate(value) {
      return this.formatDate(value, 'YYYYMMDD')
    },
    onResize: function () {
      if (window.innerWidth < 960) {
        this.isMobile = true
      } else {
        this.isMobile = false
      }
    }
  },
  beforeRouteUpdate: function (to, from, next) {
    next()
  },
  mounted: function () {
    this.initialize()
  },
  computed: {
    breadcrumbs: function () {
      let breadcrumbs = []
      if (this.info[0]) {
        breadcrumbs.push({
          title: this.$t('commons.breadcrumbs.campaignPeriodList'),
          disabled: false,
          color: 'primary',
          to: { path: `/campaignPeriodList/${this.agencyId}/${this.advertiserId}` }
        })
        breadcrumbs.push({
          title: this.$t('commons.breadcrumbs.cpxReport'),
          disabled: true
        })
      }
      return breadcrumbs
    },
    agencyId: function () {
      if (!this.$store.getters['report/info']) {
        return undefined
      }
      return this.$store.getters['report/info'].agencyId
    },
    advertiserId: function () {
      if (!this.$store.getters['report/info']) {
        return undefined
      }
      return this.$store.getters['report/info'].advertiserId
    },
    advertiserName: function () {
      if (!this.$store.getters['report/info']) {
        return undefined
      }
      return this.$store.getters['report/info'].advertiserName
    },
    campaignName: function () {
      if (!this.$store.getters['report/info']) {
        return undefined
      }
      return this.$store.getters['report/info'].campaignName
    },
    campaignId: function () {
      if (!this.$store.getters['report/info']) {
        return undefined
      }
      return this.$store.getters['report/info'].campaignId
    }
  }
}
</script>

<style scoped>
.break-word-cell {
  max-width: 500px;
  word-wrap: break-word;
}
.memo-text {
  font-size: 10px;
}
</style>
